import { inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { MessageService } from "primeng/api";
import { catchError, distinctUntilChanged, filter, lastValueFrom, map, of } from "rxjs";
import { MedicalStateActions } from "../state/medical/medical-actions";
import { plainToInstance } from "class-transformer";
import { Encounter } from "../models/encounter";
import { HubConnection } from "@microsoft/signalr";
import { UserState } from "../state/user/user-state";
import { ErrorMessage } from "../models/errors";
import { FacilityLocation } from "../models/facility";
import { MedicalBill, DraftMedicalBill, MedicalBillSubmission, RepricedMedicalBill, MedicalBillInsert, ProviderDiscount, ProviderMarkup, AdjustedMedicalBill, MarkupBasedOn, EDIPayer, EDISender } from "../models/medical-bill";
import { Procedure, Modifier, Diagnosis } from "../models/procedure";
import { ApiService } from "./api.service";
import { SignalingService } from "./hub.service";
import { BASE_URL, MEDICAL_HUB_URL } from "./injection-tokens";
import { User } from "../models/user";


export abstract class MedicalServiceBase {
  abstract getMedicalBill<E extends Encounter>(billId: number): Promise<MedicalBill<E>>;
  abstract deleteMedicalBill<E extends Encounter>(billId: number): Promise<MedicalBill<E>>;
  abstract deleteDraftMedicalBill(billId: number): Promise<DraftMedicalBill>;
  abstract submitMedicalBill(file: File, awaitCompletion: boolean): Promise<MedicalBillSubmission | DraftMedicalBill>;
  abstract getUnprocessedSubmissions(): Promise<MedicalBillSubmission[]>;
  abstract getUnconfirmedDraftMedicalBills(): Promise<DraftMedicalBill[]>;
  abstract getConfirmedMedicalBills<E extends Encounter>(): Promise<MedicalBill<E>[]>;
  abstract getRepricedBillsAwaitingPayment<E extends Encounter>(): Promise<RepricedMedicalBill<E>[]>;
  abstract getLockedRepricedBills<E extends Encounter>(): Promise<RepricedMedicalBill<E>[]>;
  abstract processSubmission(submissionId: number): Promise<MedicalBillSubmission | DraftMedicalBill>;
  abstract confirmDraftMedicalBill<E extends Encounter>(draft: DraftMedicalBill, newBill: MedicalBillInsert, encounterId: number): Promise<MedicalBill<E>>;
  abstract getEligibleEncountersForDraftBill<E extends Encounter>(draft: DraftMedicalBill): Promise<Encounter[]>;
  abstract getEligibleEncountersForPatient<E extends Encounter>(patientId: number): Promise<Encounter[]>;

  abstract getAvailableDiscountsForMedicalBill<E extends Encounter>(bill: MedicalBill<E>): Promise<ProviderDiscount[]>;
  abstract getAvailableMarkupsForMedicalBill<E extends Encounter>(bill: MedicalBill<E>): Promise<ProviderMarkup[]>;
  abstract getAdjustmentsForMedicalBill<E extends Encounter>(bill: MedicalBill<E>): Promise<AdjustedMedicalBill<E>[]>;
  abstract getRepricingsForAdjustedBill<E extends Encounter>(adjustedBill: AdjustedMedicalBill<E>): Promise<RepricedMedicalBill<E>[]>;
  abstract adjustMedicalBill<E extends Encounter>(bill: MedicalBill<E>, discount: ProviderDiscount): Promise<AdjustedMedicalBill<E>>;
  abstract adjustMedicalBillManually<E extends Encounter>(bill: MedicalBill<E>, manualAmount?: number, manualPercent?: number): Promise<AdjustedMedicalBill<E>>;
  abstract repriceMedicalBillManually<E extends Encounter>(bill: AdjustedMedicalBill<E>, basedOn: MarkupBasedOn, amount?: number, percent?: number): Promise<RepricedMedicalBill<E>>;
  abstract repriceMedicalBill<E extends Encounter>(bill: AdjustedMedicalBill<E>, markup: ProviderMarkup): Promise<RepricedMedicalBill<E>>;
  abstract voidAdjustment<E extends Encounter>(bill: AdjustedMedicalBill<E>): Promise<AdjustedMedicalBill<E>>
  abstract voidRepricing<E extends Encounter>(adjustment: AdjustedMedicalBill<E>, bill: RepricedMedicalBill<E>): Promise<RepricedMedicalBill<E>>
  abstract lockRepricing<E extends Encounter>(bill: RepricedMedicalBill<E>): Promise<RepricedMedicalBill<E>>;
  abstract calculateAdjustedMedicalBill<E extends Encounter>(bill: MedicalBill<E>, discount: ProviderDiscount): Promise<AdjustedMedicalBill<E>>;
  abstract calculateRepricedMedicalBill<E extends Encounter>(adjustedBill: AdjustedMedicalBill<E>, markup: ProviderMarkup): Promise<RepricedMedicalBill<E>>;
  abstract calculateManuallyAdjustedMedicalBill<E extends Encounter>(bill: MedicalBill<E>, manualAmount: number | undefined, manualPercent: number | undefined): Promise<AdjustedMedicalBill<E>>
  abstract calculateManuallyRepricedMedicalBill<E extends Encounter>(adjustedBill: AdjustedMedicalBill<E>, manualAmount: number | undefined, manualPercent: number | undefined, basedOn: MarkupBasedOn): Promise<RepricedMedicalBill<E>>;
  abstract submitMedicalBillToClearingHouse<E extends Encounter>(repricedBil: RepricedMedicalBill<E>, payer: EDIPayer, sender: EDISender): Promise<RepricedMedicalBill<E> | ErrorMessage>;
  abstract isBillDuplicate(patientAccount: string, serviceDate: Date, totalCharges: number, codes: string[]): Promise<boolean>;
  abstract getPayers(): Promise<EDIPayer[]>;
  abstract getSenders(): Promise<EDISender[]>;
  abstract searchProcedures(q: string): Promise<Procedure[]>;
  abstract searchFacilities(q: string, includeBilling: boolean): Promise<FacilityLocation[]>;
  abstract searchProviders(filter: ProviderFilter): Promise<FacilityLocation[]>;
  abstract searchModifiers(q: string): Promise<Modifier[]>;
  abstract searchDiagnoses(q: string): Promise<Diagnosis[]>;

}

export class ProviderFilter {
  name?: string;
  ein?: string;
  location?: string;
}


@Injectable({
  providedIn: 'root'
})
export abstract class MedicalService extends MedicalServiceBase {
  protected _api: ApiService = inject(ApiService);
  protected _store: Store = inject(Store);
  protected _messages: MessageService = inject(MessageService);
  protected _signaling: SignalingService = inject(SignalingService);
  protected _baseUrl: string = inject(BASE_URL);
  protected _medicalHubUrl: string = inject(MEDICAL_HUB_URL);

  constructor
    (
  ) {
    super();
    this._medicalHub = this._signaling.generateHub(this._medicalHubUrl);

    this.currentUser$
      .pipe(
        filter((cu): cu is User => cu != null && cu.id != null),
        distinctUntilChanged((a, b) => a.id === b.id)
      )
      .subscribe(
        cu => {
          if (cu?.id != null)
          {
            this._signaling.startHub(this._medicalHub);
          }
          else
          {
            this._medicalHub.stop().catch(err => console.error('Hub stop failed', err));
          }
        }
      );

    this._medicalHub.on("MedicalBillBeingAnalyzed", (submission: MedicalBillSubmission) => this._store.dispatch(new MedicalStateActions.MedicalBillProcessorMedicalBillBeingAnalyzed({ submission: plainToInstance(MedicalBillSubmission, submission) })));
    this._medicalHub.on("IdentifyingPatient", (submission: MedicalBillSubmission) => this._store.dispatch(new MedicalStateActions.MedicalBillProcessorIdentifyingPatient({ submission: plainToInstance(MedicalBillSubmission, submission) })));
    this._medicalHub.on("IdentifyingProviders", (submission: MedicalBillSubmission) => this._store.dispatch(new MedicalStateActions.MedicalBillProcessorIdentifyingProviders({ submission: plainToInstance(MedicalBillSubmission, submission) })));
    this._medicalHub.on("ProcessingLines", (submission: MedicalBillSubmission) => this._store.dispatch(new MedicalStateActions.MedicalBillProcessorProcessingLines({ submission: plainToInstance(MedicalBillSubmission, submission) })));
    this._medicalHub.on("MedicalBillProcessingComplete", (submission: MedicalBillSubmission, draftBill: DraftMedicalBill, succeeded: boolean) => this._store.dispatch(new MedicalStateActions.MedicalBillProcessorMedicalBillProcessingComplete({ submission: plainToInstance(MedicalBillSubmission, submission), draftBill: plainToInstance(DraftMedicalBill, draftBill), succeeded: succeeded })));
    this._medicalHub.on("MedicalBillConfirmed", (bill: MedicalBill<Encounter>, draft: DraftMedicalBill) => new MedicalStateActions.MedicalBillServiceMedicalBillConfirmed({ draft: draft, medicalBill: bill }));
    this._medicalHub.on("MedicalBillAdjusted", (bill: MedicalBill<Encounter>, adjustedBill: AdjustedMedicalBill<Encounter>) => new MedicalStateActions.MedicalBillAdjusted({ bill: bill, adjustedBill: adjustedBill }));
    this._medicalHub.on("MedicalBillAdjustmentVoided", (bill: MedicalBill<Encounter>, adjustedBill: AdjustedMedicalBill<Encounter>) => new MedicalStateActions.AdjustedBillVoided({ adjustedBill: adjustedBill }));
    this._medicalHub.on("MedicalBillAdjustmentRepriced", (adjustedBill: AdjustedMedicalBill<Encounter>, repricedBill: RepricedMedicalBill<Encounter>) => new MedicalStateActions.AdjustedBillRepriced({ adjustedBill: adjustedBill, repricedBill: repricedBill }));
    this._medicalHub.on("MedicalBillRepricingVoided", (adjustedBill: AdjustedMedicalBill<Encounter>, repricedBill: RepricedMedicalBill<Encounter>) => new MedicalStateActions.RepricedBillVoided({ repricedBill: repricedBill }));
    this._medicalHub.on("MedicalBillRepricingLocked", (repricedBill: RepricedMedicalBill<Encounter>) => new MedicalStateActions.RepricedBillLocked({ repricedBill: repricedBill }));
  }

  private _medicalHub: HubConnection;
  currentUser$ = this._store.select(UserState.currentUser);

  override isBillDuplicate(patientAccount: string, serviceDate: Date, totalCharges: number, codes: string[]): Promise<boolean> {
    //Call up the web socket to get the result
    return this._medicalHub.invoke("IsBillDuplicate", patientAccount, serviceDate, totalCharges, codes);
  }

  override getMedicalBill<E extends Encounter>(billId: number): Promise<MedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<MedicalBill<E>>(this._baseUrl, `/medical/bills/${billId}`, {})
          .pipe
          (
            map
              (
                b => plainToInstance(MedicalBill<E>, b)
              )
          )
      )
  }

  override deleteMedicalBill<E extends Encounter>(billId: number): Promise<MedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIDeleteCall<MedicalBill<E>>(this._baseUrl, `/medical/bills/confirmed/${billId}`, {})
          .pipe
          (
            map
              (
                b => {
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Medical Bill Successfully Deleted' });
                  this._store.dispatch(new MedicalStateActions.MedicalBillDeleted({ bill: plainToInstance(MedicalBill<E>, b) }));
                  return plainToInstance(MedicalBill<E>, b);
                }
              )
          )
      )
  }

  override deleteDraftMedicalBill(billId: number): Promise<DraftMedicalBill> {
    return lastValueFrom
      (
        this._api.makeAPIDeleteCall<DraftMedicalBill>(this._baseUrl, `/medical/bills/drafts/${billId}`, {})
          .pipe
          (
            map
              (
                b => {
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Medical Bill Successfully Deleted' });
                  this._store.dispatch(new MedicalStateActions.DraftMedicalBillDeleted({ bill: plainToInstance(DraftMedicalBill, b) }));
                  return plainToInstance(DraftMedicalBill, b);
                }
              )
          )
      )
  }




  override processSubmission(submissionId: number): Promise<MedicalBillSubmission | DraftMedicalBill> {
    return lastValueFrom
      (
        this._api.makeAPIPostCall<MedicalBillSubmission | DraftMedicalBill>(this._baseUrl, `/medical/bills/submissions/${submissionId}/process`, {})
          .pipe
          (
            map
              (
                f => {
                  if (typeof f == 'object' && 'submittedById' in f && 'dateSubmitted' in f) {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Medical Bill Successfully Submitted' });
                    this._store.dispatch(new MedicalStateActions.MedicalBillProcessorMedicalBillSubmitted({ submission: f as MedicalBillSubmission }));

                    return plainToInstance(MedicalBillSubmission, f);
                  }

                  else {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Medical Bill Completed' });

                    return plainToInstance(DraftMedicalBill, f);
                  }

                }
              )
          ));
  }

  override submitMedicalBill(file: File, awaitCompletion: boolean = false): Promise<MedicalBillSubmission | DraftMedicalBill> {

    var d: FormData = new FormData();
    d.append('billImageFile', file);
    return lastValueFrom
      (
        this._api.makeAPIPutCall<MedicalBillSubmission | DraftMedicalBill>(this._baseUrl, "/medical/bills/fromfile", d,
          {
            ...awaitCompletion && { awaitCompletion: awaitCompletion.toString() }
          })
          .pipe
          (
            map
              (
                f => {
                  if (typeof f == 'object' && 'submittedById' in f && 'dateSubmitted' in f) {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Medical Bill Successfully Submitted' });
                    this._store.dispatch(new MedicalStateActions.MedicalBillProcessorMedicalBillSubmitted({ submission: f as MedicalBillSubmission }));

                    return plainToInstance(MedicalBillSubmission, f);

                  }

                  else {
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Medical Bill Completed' });

                    return plainToInstance(DraftMedicalBill, f);

                  }

                  return f;
                }
              )
          )
      )


  }


  override getUnprocessedSubmissions(): Promise<MedicalBillSubmission[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<MedicalBillSubmission[]>(this._baseUrl, '/medical/bills/submissions', {})
          .pipe
          (
            map
              (
                b => plainToInstance(MedicalBillSubmission, b)
              )
          )
      );
  }

  override getUnconfirmedDraftMedicalBills(): Promise<DraftMedicalBill[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<DraftMedicalBill[]>(this._baseUrl, '/medical/bills/drafts', {})
          .pipe
          (
            map
              (
                b => plainToInstance(DraftMedicalBill, b)
              )
          )
      );
  }

  override getRepricedBillsAwaitingPayment<E extends Encounter>(): Promise<RepricedMedicalBill<E>[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<RepricedMedicalBill<E>[]>(this._baseUrl, '/medical/bills/repriced/awaitingPayment', {})
          .pipe
          (
            map
              (
                b => plainToInstance(RepricedMedicalBill<E>, b)
              )
          )
      );
  }

  override getLockedRepricedBills<E extends Encounter>(): Promise<RepricedMedicalBill<E>[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<RepricedMedicalBill<E>[]>(this._baseUrl, '/medical/bills/repriced/locked', {})
          .pipe
          (
            map
              (
                b => plainToInstance(RepricedMedicalBill<E>, b)
              )
          )
      );
  }




  override getConfirmedMedicalBills<E extends Encounter>(): Promise<MedicalBill<E>[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<MedicalBill<E>[]>(this._baseUrl, '/medical/bills/confirmed', {})
          .pipe
          (
            map
              (
                b => plainToInstance(MedicalBill<E>, b)
              )
          )
      )
  }



  override getAdjustmentsForMedicalBill<E extends Encounter>(bill: MedicalBill<E>): Promise<AdjustedMedicalBill<E>[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<AdjustedMedicalBill<E>[]>(this._baseUrl, `/medical/bills/${bill.id}/adjustments`, {})
          .pipe
          (
            map
              (
                b => plainToInstance(AdjustedMedicalBill<E>, b)
              )
          )
      )
  }



  override getRepricingsForAdjustedBill<E extends Encounter>(adjustedBill: AdjustedMedicalBill<E>): Promise<RepricedMedicalBill<E>[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<RepricedMedicalBill<E>[]>(this._baseUrl, `/medical/bills/${adjustedBill.originalBillId}/adjustments/${adjustedBill.id}/markups`, {})
          .pipe
          (
            map
              (
                b => plainToInstance(RepricedMedicalBill<E>, b)
              )
          )
      )
  }



  override searchProcedures(q: string): Promise<Procedure[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<Procedure[]>(this._baseUrl, "/medical/procedures", { query: q })
          .pipe(map(procedures => procedures.map(p => new Procedure(p.id, p.code, p.description, p.codeSet, p.category))))
      );
  }

  override searchFacilities(q: string, includeBilling: boolean): Promise<FacilityLocation[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<FacilityLocation[]>(this._baseUrl, "/medical/facilities", { query: q, includeBilling: includeBilling.toString() })
          .pipe(map(procedures => procedures.map(p => new FacilityLocation(p.facility, p.location))))
      );
  }

  override searchProviders(filter: ProviderFilter): Promise<FacilityLocation[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<FacilityLocation[]>(this._baseUrl, "/medical/providers",
          {
            ...filter.name && { name: filter.name },
            ...filter.ein && { ein: filter.ein },
            ...filter.location && { location: filter.location }
          })
          .pipe(map(results => results.map(r => new FacilityLocation(r.facility, r.location))))
      );
  }

  override searchModifiers(q: string): Promise<Modifier[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<Modifier[]>(this._baseUrl, "/medical/modifiers", { query: q })
          .pipe(map(modifiers => modifiers.map(m => new Modifier(m.code, m.description, m.modifierMeaning))))
      )
  }

  override searchDiagnoses(q: string): Promise<Diagnosis[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<Diagnosis[]>(this._baseUrl, "/medical/diagnoses", { query: q })
          .pipe(map(diagnoses => diagnoses.map(d => new Diagnosis(d.code, d.description))))
      )
  }

  override confirmDraftMedicalBill<E extends Encounter>(draft: DraftMedicalBill, newBill: MedicalBillInsert, encounterId: number): Promise<MedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIPostCall<MedicalBill<E>>(this._baseUrl, `/medical/bills/drafts/${draft.id}/confirm/forEncounter/${encounterId}`, newBill, {})
          .pipe
          (
            map
              (
                b => {
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Medical Bill Completed' });
                  this._store.dispatch(new MedicalStateActions.MedicalBillServiceMedicalBillConfirmed({ draft: plainToInstance(DraftMedicalBill, draft), medicalBill: plainToInstance(MedicalBill<E>, b) }))
                  return b;
                }
              )
          )
      );
  }

  override getAvailableDiscountsForMedicalBill<E extends Encounter>(bill: MedicalBill<E>): Promise<ProviderDiscount[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<ProviderDiscount[]>(this._baseUrl, `/medical/bills/${bill.id}/availablediscounts`, {})
          .pipe
          (
            map
              (
                d => plainToInstance(ProviderDiscount, d)
              )
          )
      )
  }

  override getAvailableMarkupsForMedicalBill<E extends Encounter>(bill: MedicalBill<E>): Promise<ProviderMarkup[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<ProviderMarkup[]>(this._baseUrl, `/medical/bills/${bill.id}/availablemarkups`, {})
          .pipe
          (
            map
              (
                d => plainToInstance(ProviderMarkup, d)
              )
          )
      )
  }



  override adjustMedicalBill<E extends Encounter>(bill: MedicalBill<E>, discount: ProviderDiscount): Promise<AdjustedMedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIPostCall<AdjustedMedicalBill<E>>(this._baseUrl, `/medical/bills/${bill.id}/adjust/${discount.id}`, {}, {})
          .pipe
          (
            map
              (
                b => {
                  const val = plainToInstance(AdjustedMedicalBill<E>, b);
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Successfully Adjusted' });
                  this._store.dispatch(new MedicalStateActions.MedicalBillAdjusted({ bill: bill, adjustedBill: val }));
                  return val;
                }
              )
          )
      )
  }

  override adjustMedicalBillManually<E extends Encounter>(bill: MedicalBill<E>, manualAmount?: number, manualPercent?: number): Promise<AdjustedMedicalBill<E>> {
    const manualReq: ManualAdjustmentRequest = { discountAmount: manualAmount, discountPercent: manualPercent }
    return lastValueFrom(
      this._api.makeAPIPostCall<AdjustedMedicalBill<E>>(this._baseUrl, `/medical/bills/${bill.id}/adjust/manual`, manualReq, {})
        .pipe
        (
          map
            (
              b => {
                const val = plainToInstance(AdjustedMedicalBill<E>, b);
                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Successfully Adjusted' });
                this._store.dispatch(new MedicalStateActions.MedicalBillAdjusted({ bill: bill, adjustedBill: val }));
                return val;
              }
            )
        )
    )
  }


  override repriceMedicalBillManually<E extends Encounter>(bill: AdjustedMedicalBill<E>, basedOn: MarkupBasedOn, amount?: number, percent?: number): Promise<RepricedMedicalBill<E>> {
    const manualReq: ManualMarkupRequest = { markupAmount: amount, markupPercent: percent, markupBasedOn: basedOn }

    return lastValueFrom
      (
        this._api.makeAPIPostCall<RepricedMedicalBill<E>>(this._baseUrl, `/medical/bills/${bill.originalBillId}/adjustments/${bill.id}/reprice/manual`, manualReq, {})
          .pipe
          (
            map
              (
                b => {
                  const val = plainToInstance(RepricedMedicalBill<E>, b);
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Successfully Repriced' });
                  this._store.dispatch(new MedicalStateActions.AdjustedBillRepriced({ adjustedBill: bill, repricedBill: val }))
                  return val;
                }
              )
          )
      )
  }

  override repriceMedicalBill<E extends Encounter>(bill: AdjustedMedicalBill<E>, markup: ProviderMarkup): Promise<RepricedMedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIPostCall<RepricedMedicalBill<E>>(this._baseUrl, `/medical/bills/${bill.originalBillId}/adjustments/${bill.id}/reprice/${markup.id}`, {}, {})
          .pipe
          (
            map
              (
                b => {
                  const val = plainToInstance(RepricedMedicalBill<E>, b);
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Successfully Repriced' });
                  this._store.dispatch(new MedicalStateActions.AdjustedBillRepriced({ adjustedBill: bill, repricedBill: val }));
                  return val;
                }
              )
          )
      )
  }



  override voidAdjustment<E extends Encounter>(bill: AdjustedMedicalBill<E>): Promise<AdjustedMedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIPostCall<AdjustedMedicalBill<E>>(this._baseUrl, `/medical/bills/${bill.originalBillId}/adjustments/${bill.id}/void`, {}, {})
          .pipe
          (
            map
              (
                b => {
                  const val = plainToInstance(AdjustedMedicalBill<E>, b);
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Adjustment Successfully Voided' });
                  this._store.dispatch(new MedicalStateActions.AdjustedBillVoided({ adjustedBill: val }));
                  return val;
                }
              )
          )
      )
  }


  override voidRepricing<E extends Encounter>(adjustment: AdjustedMedicalBill<E>, bill: RepricedMedicalBill<E>): Promise<RepricedMedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIPostCall<RepricedMedicalBill<E>>(this._baseUrl, `/medical/bills/${adjustment.originalBillId}/adjustments/${adjustment.id}/markups/${bill.id}/void`, {}, {})
          .pipe
          (
            map
              (
                b => {
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Repricing Successfully Voided' });
                  const val = plainToInstance(RepricedMedicalBill<E>, b);
                  this._store.dispatch(new MedicalStateActions.RepricedBillVoided({ repricedBill: val }));
                  return val;
                }
              )
          )
      )

  }

  override lockRepricing<E extends Encounter>(bill: RepricedMedicalBill<E>): Promise<RepricedMedicalBill<E>> {
    return lastValueFrom
      (
        this._api.makeAPIPostCall<RepricedMedicalBill<E>>(this._baseUrl, `/medical/bills/repriced/${bill.id}/lock`, {}, {})
          .pipe
          (
            map
              (
                b => {
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Repricing Successfully Locked' });
                  const val = plainToInstance(RepricedMedicalBill<E>, b);
                  this._store.dispatch(new MedicalStateActions.RepricedBillLocked({ repricedBill: val }));
                  return val;
                }
              )
          )
      )

  }


  override calculateAdjustedMedicalBill<E extends Encounter>(bill: MedicalBill<E>, discount: ProviderDiscount): Promise<AdjustedMedicalBill<E>> {
    return this._medicalHub.invoke<AdjustedMedicalBill<E>>("CalculateAdjustedBill", bill.id, discount.id);
  }

  override calculateManuallyAdjustedMedicalBill<E extends Encounter>(bill: MedicalBill<E>, manualAmount: number | undefined, manualPercent: number | undefined): Promise<AdjustedMedicalBill<E>> {
    return this._medicalHub.invoke<AdjustedMedicalBill<E>>("CalculateManuallyAdjustedBill", bill.id, manualPercent, manualAmount);
  }

  override calculateRepricedMedicalBill<E extends Encounter>(adjustedBill: AdjustedMedicalBill<E>, markup: ProviderMarkup): Promise<RepricedMedicalBill<E>> {
    return this._medicalHub.invoke<RepricedMedicalBill<E>>("CalculateRepricedBill", adjustedBill.id, markup.id);
  }

  override calculateManuallyRepricedMedicalBill<E extends Encounter>(adjustedBill: AdjustedMedicalBill<E>, manualAmount: number | undefined, manualPercent: number | undefined, basedOn: MarkupBasedOn): Promise<RepricedMedicalBill<E>> {
    return this._medicalHub.invoke<RepricedMedicalBill<E>>("CalculateManuallyRepricedBill", adjustedBill.id, manualPercent, manualAmount, basedOn);
  }

  override submitMedicalBillToClearingHouse<E extends Encounter>(repricedBil: RepricedMedicalBill<E>, payer: EDIPayer, sender: EDISender): Promise<RepricedMedicalBill<E> | ErrorMessage> {
    var chr = { payerId: payer.id, senderId: sender.id } as ClearingHouseRequest;
    return lastValueFrom
      (
        this._api.makeAPIPostCall<RepricedMedicalBill<E>>(this._baseUrl, `/medical/bills/${repricedBil.originalBillId}/adjustments/${repricedBil.adjustmentId}/markups/${repricedBil.id}/submitToClearinghouse`, chr, {})
          .pipe
          (
            map
              (
                b => {
                  this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Bill Successfully Sent to Clearing House' });
                  const val = plainToInstance(RepricedMedicalBill<E>, b);
                  this._store.dispatch(new MedicalStateActions.BillSentToClearingHouse({ repricedBill: val }));
                  return val;
                }
              ),
            catchError((error: ErrorMessage) => { return of(error) })
          )
      )
  }


  override getPayers(): Promise<EDIPayer[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<EDIPayer[]>(this._baseUrl, "/medical/x12/payers", {})
          .pipe(map(p => plainToInstance(EDIPayer, p)))
      )

  }
  override getSenders(): Promise<EDISender[]> {
    return lastValueFrom
      (
        this._api.makeAPIGetCall<EDISender[]>(this._baseUrl, "/medical/x12/senders", {})
          .pipe(map(s => plainToInstance(EDISender, s)))
      )

  }


}

class ManualAdjustmentRequest {
  discountAmount?: number;
  discountPercent?: number;
}

class ManualMarkupRequest {
  markupAmount?: number;
  markupPercent?: number;
  markupBasedOn: MarkupBasedOn = MarkupBasedOn.AdjustedAmount;
}

class ClearingHouseRequest {
  senderId?: number;
  payerId?: number
}